import { addDays, formatCalendarDate, isValidCalendarDate, parseCalendarDate, toDashed } from '@lib/datetime'
import { countLabel } from '@/utils/plural'
import { DaySpan, destinationName, spanIndexForDay } from './dayHeader'

/**
 * How destinations colour the itinerary, mirroring iOS `TripStyle.segmentColors`:
 * three colours cycling by destination order, and a day no destination owns
 * painted with the first colour so legacy trips read as one-destination trips.
 */
const DESTINATION_COLOR_COUNT = 3

export const colorIndexOfDestination = (position: number): number =>
	((position % DESTINATION_COLOR_COUNT) + DESTINATION_COLOR_COUNT) % DESTINATION_COLOR_COUNT

export const destinationColorIndex = (spans: DaySpan[], dayIndex: number): number => {
	const position = spanIndexForDay(spans, dayIndex)
	return position === -1 ? 0 : colorIndexOfDestination(position)
}

/** What the legend needs of a trip destination. */
export interface LegendDestination {
	id: number
	startDayIndex: number
	dayCount: number
	location: { primaryText: string | null; secondaryText: string | null; fullText: string }
}

export interface LegendRow {
	id: number
	name: string
	/** The location's secondary text — the region or country the place sits in — when it has one. */
	detail: string | null
	/** "Oct 3 – Oct 9", "Oct 10" for a single day, "Days 1 – 3" on an undated trip. */
	dateRange: string
	colorIndex: number
	/** The destination's first trip day, zero-based: where picking the row takes the itinerary. */
	startDayIndex: number
}

const monthDay = (date: string): string => formatCalendarDate(date, 'monthDay')

export const legendRows = (destinations: LegendDestination[], startDate: string | null | undefined): LegendRow[] => {
	const dated = isValidCalendarDate(startDate)
	return destinations.map((d, position) => {
		const first = d.startDayIndex
		const last = d.startDayIndex + d.dayCount - 1
		const dateRange = dated
			? d.dayCount === 1
				? monthDay(addDays(startDate, first))
				: `${monthDay(addDays(startDate, first))} – ${monthDay(addDays(startDate, last))}`
			: d.dayCount === 1
				? `Day ${first + 1}`
				: `Days ${first + 1} – ${last + 1}`
		const detail = d.location.secondaryText?.trim() || null
		return {
			id: d.id,
			name: destinationName(d.location),
			detail,
			dateRange,
			colorIndex: colorIndexOfDestination(position),
			startDayIndex: first,
		}
	})
}

export interface TripSummary {
	/** "Oct 3 – Oct 23, 2026", or "No dates yet". */
	range: string
	/** "21 days · 3 destinations"; a trip to one place (or none yet) is just its days. */
	detail: string
}

export const tripSummary = ({
	startDate,
	dayCount,
	destinationCount,
}: {
	startDate: string | null | undefined
	dayCount: number
	destinationCount: number
}): TripSummary => {
	const days = countLabel(dayCount, 'day')
	const detail = destinationCount > 1 ? `${days} · ${countLabel(destinationCount, 'destination')}` : days
	if (!isValidCalendarDate(startDate)) return { range: 'No dates yet', detail }
	const start = toDashed(startDate)
	const end = addDays(start, Math.max(dayCount - 1, 0))
	const sameYear = parseCalendarDate(start)?.year === parseCalendarDate(end)?.year
	const range = sameYear
		? `${monthDay(start)} – ${formatCalendarDate(end, 'medium')}`
		: `${formatCalendarDate(start, 'medium')} – ${formatCalendarDate(end, 'medium')}`
	return { range, detail }
}

/**
 * The legend's pages. The legend never outgrows the room under the calendar,
 * so a trip with more destinations than fit is paged; selecting a day turns to
 * the page holding that day's destination.
 */
export interface LegendMetrics {
	rowHeight: number
	rowGap: number
	/** The pager's own height plus the gap above it. */
	pagerHeight: number
}

/**
 * How many rows a page holds in `availableHeight`. Every row when they all fit,
 * since then no pager is drawn; otherwise what fits above the pager, never
 * fewer than one. `fallback` answers where nothing was measured (no layout yet).
 */
export const legendPageSize = (
	availableHeight: number,
	rowCount: number,
	metrics: LegendMetrics,
	fallback: number,
): number => {
	if (rowCount <= 0) return 1
	if (!(availableHeight > 0)) return Math.max(1, fallback)
	const stride = metrics.rowHeight + metrics.rowGap
	const fits = (height: number) => Math.floor((height + metrics.rowGap) / stride)
	if (fits(availableHeight) >= rowCount) return rowCount
	return Math.max(1, fits(availableHeight - metrics.pagerHeight))
}

export const legendPageCount = (rowCount: number, pageSize: number): number =>
	Math.max(1, Math.ceil(rowCount / Math.max(1, pageSize)))

/** The page holding the row at `rowIndex`; page 0 when there is no such row. */
export const legendPageOfRow = (rowIndex: number, pageSize: number): number =>
	rowIndex < 0 ? 0 : Math.floor(rowIndex / Math.max(1, pageSize))

/** A page number held inside the pages that exist, so a shrinking list never strands the reader. */
export const clampLegendPage = (page: number, rowCount: number, pageSize: number): number =>
	Math.min(Math.max(0, page), legendPageCount(rowCount, pageSize) - 1)

export const legendPageRows = <R>(rows: R[], page: number, pageSize: number): R[] => {
	const size = Math.max(1, pageSize)
	return rows.slice(page * size, page * size + size)
}
